import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { useAuth } from './AuthContext';
import { useWebSocket } from './WebSocketContext';
import { fetchTenantPressureMetrics } from '../services/tenantMetrics';
import { transformKeys } from '../services/transformers';
import { logTenantMetricsDebug } from '../utils/tenantMetricsDebug';
import type {
  TenantPressureMetricsProductSlice,
  TenantPressureMetricsResponse,
} from '../types';

interface TenantPressureMetricsContextValue {
  isConnected: boolean;
  isSubscribed: boolean;
  isRefreshing: boolean;
  ai: TenantPressureMetricsProductSlice | null;
  reverseIvr: TenantPressureMetricsProductSlice | null;
  lastPayload: TenantPressureMetricsResponse | null;
  canViewPressureMetrics: boolean;
  refresh: (reason?: string) => Promise<void>;
}

const TenantPressureMetricsContext = createContext<TenantPressureMetricsContextValue | undefined>(undefined);

/**
 * Provide one shared tenant pressure metrics store for shell consumers.
 */
export function TenantPressureMetricsProvider({ children }: { children: ReactNode }) {
  const { currentTenant, user, capabilities } = useAuth();
  const { state, addObserver, removeObserver } = useWebSocket();
  const tenantId = currentTenant?.id || user?.tenantId || null;
  const canViewPressureMetrics = Boolean(capabilities?.canViewPressureMetrics);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [ai, setAi] = useState<TenantPressureMetricsProductSlice | null>(null);
  const [reverseIvr, setReverseIvr] = useState<TenantPressureMetricsProductSlice | null>(null);
  const [lastPayload, setLastPayload] = useState<TenantPressureMetricsResponse | null>(null);
  const logDebug = useCallback((message: string, data?: unknown) => {
    logTenantMetricsDebug('TenantPressureMetricsProvider', message, data);
  }, []);

  useEffect(() => {
    logDebug('tenant changed, resetting cached pressure metrics state', { tenantId });
    setAi(null);
    setReverseIvr(null);
    setLastPayload(null);
  }, [logDebug, tenantId]);

  const refresh = useCallback(async (reason = 'manual_recovery') => {
    if (!tenantId || !canViewPressureMetrics) {
      logDebug('refresh skipped because tenant or permission is missing', {
        reason,
        tenantId,
        canViewPressureMetrics,
      });
      return;
    }

    logDebug('refresh started', {
      reason,
      tenantId,
      isConnected: state.isConnected,
      hasLastPayload: Boolean(lastPayload),
    });
    setIsRefreshing(true);
    try {
      const response = await fetchTenantPressureMetrics();
      setAi(response.ai ?? null);
      setReverseIvr(response.reverseIvr ?? null);
      setLastPayload(response);
      logDebug('refresh completed', {
        reason,
        tenantId,
        hasAi: Boolean(response.ai),
        hasReverseIvr: Boolean(response.reverseIvr),
      });
    } catch (error) {
      logDebug('refresh failed', {
        reason,
        tenantId,
        error,
      });
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [canViewPressureMetrics, lastPayload, logDebug, state.isConnected, tenantId]);

  const handleEvent = useCallback((data: TenantPressureMetricsResponse, action: string) => {
    if (action !== 'create' && action !== 'update' && action !== 'delete') {
      logDebug('event ignored because action is not handled', { tenantId, action, data });
      return;
    }

    const normalized = transformKeys<TenantPressureMetricsResponse>(data);
    logDebug('event received', {
      tenantId,
      action,
      hasAi: Boolean(normalized.ai),
      hasReverseIvr: Boolean(normalized.reverseIvr),
      payload: normalized,
    });
    setAi(normalized.ai ?? null);
    setReverseIvr(normalized.reverseIvr ?? null);
    setLastPayload(normalized);
  }, [logDebug, tenantId]);

  useEffect(() => {
    if (!tenantId || !canViewPressureMetrics || !state.isConnected) {
      logDebug('subscription skipped because preconditions are not satisfied', {
        tenantId,
        canViewPressureMetrics,
        isConnected: state.isConnected,
      });
      setIsSubscribed(false);
      return;
    }

    logDebug('creating pressure metrics observer subscription', {
      tenantId,
      canViewPressureMetrics,
      isConnected: state.isConnected,
    });
    const observerId = addObserver(
      'tenant_pressure_metrics',
      { data: { target: tenantId } },
      handleEvent,
      {
        subscribeAction: 'subscribe',
        unsubscribeAction: 'unsubscribe',
        includeCreateEvents: true,
        includeUpdateEvents: true,
        includeDeleteEvents: true,
      },
    );
    setIsSubscribed(true);
    logDebug('pressure metrics observer subscribed', { tenantId, observerId });

    return () => {
      logDebug('cleaning up pressure metrics observer subscription', { tenantId, observerId });
      removeObserver(observerId);
      setIsSubscribed(false);
    };
  }, [addObserver, canViewPressureMetrics, handleEvent, logDebug, removeObserver, state.isConnected, tenantId]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const refreshIfRecoveryIsNeeded = () => {
      if (!tenantId || !canViewPressureMetrics) {
        logDebug('recovery skipped because tenant or permission is missing', {
          tenantId,
          canViewPressureMetrics,
        });
        return;
      }
      if (state.isConnected && lastPayload) {
        logDebug('recovery skipped because provider believes connection is healthy', {
          tenantId,
          isConnected: state.isConnected,
          hasLastPayload: Boolean(lastPayload),
        });
        return;
      }
      logDebug('recovery refresh requested', {
        tenantId,
        isConnected: state.isConnected,
        hasLastPayload: Boolean(lastPayload),
      });
      void refresh('pressure_metrics_recovery');
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        logDebug('visibilitychange ignored because document is not visible', {
          tenantId,
          documentVisibilityState: document.visibilityState,
        });
        return;
      }
      logDebug('visibilitychange detected visible document for recovery check', { tenantId });
      refreshIfRecoveryIsNeeded();
    };

    window.addEventListener('focus', refreshIfRecoveryIsNeeded);
    window.addEventListener('pageshow', refreshIfRecoveryIsNeeded);
    window.addEventListener('online', refreshIfRecoveryIsNeeded);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('focus', refreshIfRecoveryIsNeeded);
      window.removeEventListener('pageshow', refreshIfRecoveryIsNeeded);
      window.removeEventListener('online', refreshIfRecoveryIsNeeded);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [canViewPressureMetrics, lastPayload, logDebug, refresh, state.isConnected, tenantId]);

  const value = useMemo<TenantPressureMetricsContextValue>(
    () => ({
      isConnected: state.isConnected,
      isSubscribed,
      isRefreshing,
      ai,
      reverseIvr,
      lastPayload,
      canViewPressureMetrics,
      refresh,
    }),
    [
      ai,
      canViewPressureMetrics,
      isRefreshing,
      isSubscribed,
      lastPayload,
      refresh,
      reverseIvr,
      state.isConnected,
    ],
  );

  return (
    <TenantPressureMetricsContext.Provider value={value}>
      {children}
    </TenantPressureMetricsContext.Provider>
  );
}

export function useTenantPressureMetricsContext(): TenantPressureMetricsContextValue {
  const context = useContext(TenantPressureMetricsContext);
  if (!context) {
    throw new Error('useTenantPressureMetricsContext must be used within TenantPressureMetricsProvider');
  }
  return context;
}
